import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

/**
 * TypedQuizNote — types the freeform note INSIDE the add-people quiz
 * screenshot (Kate 2026-07-21, /welcome2 "Tell Thea once." card): a white
 * patch covers the screenshot's baked-in textarea text and the same line
 * types itself over it when the phone scrolls into view — the social-post
 * typing treatment, in situ.
 *
 * Rendered through BeatDevice's `screenOverlay` slot, inside its ImageArea.
 * The ImagePlane wrapper reproduces the screenshot's intrinsic aspect ratio
 * at the rendered width, so the patch geometry (fractions of the intrinsic
 * 640×1072 image) lands on the textarea at every device size; `cqw` units
 * scale the type with the image. The patch sits INSIDE the textarea's
 * border, so the screenshot's own border/radius keep showing.
 *
 * GEOMETRY IS COUPLED TO beats/add-people.png — if that screenshot is
 * re-captured, re-measure the textarea box below.
 *
 * The typing runs ONCE per mount (Kate 2026-10-20, THE-1080 #818 review:
 * "type once and hold, but only start the typing once it scrolls into
 * view"): type, then hold the finished note — no clear-and-retype loop.
 * The caret keeps blinking while typing (it's mid-sentence) but settles —
 * stops animating, stays solid — once the note is complete, so nothing on
 * the finished frame blinks longer than WCAG 2.2.2's 5s allowance.
 *
 * Motion rules (testing.md "Happo flake sources" + BoardChipTabs precedent):
 * under prefers-reduced-motion or navigator.webdriver the full line renders
 * statically with no caret — visually identical to the untouched screenshot,
 * so Happo photographs a deterministic frame and reduced-motion users see
 * the static text.
 *
 * A11y: the screenshot's alt text already narrates this screen; the overlay
 * is presentation-only and fully aria-hidden.
 */

export const TYPE_INTERVAL_MS = 45;
export const TYPE_START_DELAY_MS = 350;
// How long the ratio must stay >= 0.5 before typing arms (Kate #818 re-QA on
// c38d6503): on load, a not-yet-settled layout can genuinely put the note at
// 100% visible for one frame, then shift it down to a settled 20% a moment
// later. A one-shot "first qualifying frame" start can't tell that apart from
// a real scroll-into-view, so require the ratio to hold for a short beat —
// long enough to outlast the load-time layout shift the trace showed
// resolving within ~130ms, short enough that a genuine scroll-into-view still
// feels instant.
export const VISIBILITY_DWELL_MS = 200;

/* Freeform textarea geometry, measured on beats/add-people.png (640×1072).
   Box = the textarea INTERIOR (inset past the 1px border). Text metrics are
   intrinsic px converted to fractions of the image width. */
const IMG_W = 640;
const IMG_H = 1072;
const BOX_LEFT = 70 / IMG_W;
const BOX_TOP = 806 / IMG_H;
const BOX_WIDTH = 500 / IMG_W;
const BOX_HEIGHT = 150 / IMG_H;
const PAD_X_CQW = (16 / IMG_W) * 100; /* text inset from the box edge */
const PAD_Y_CQW = (16 / IMG_W) * 100;
const FONT_CQW = (28 / IMG_W) * 100; /* 28px type at the 640px intrinsic width */
const LINE_HEIGHT = 38 / 28;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

/* Anchors the overlay to the IMAGE's coordinate space: same top-left, same
   width, same aspect as the screenshot ScreenImg renders at. container-type
   makes cqw units track the rendered image width. */
const ImagePlane = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  aspect-ratio: ${IMG_W} / ${IMG_H};
  container-type: inline-size;
  pointer-events: none;
`;

const Patch = styled.div`
  position: absolute;
  left: ${BOX_LEFT * 100}%;
  top: ${BOX_TOP * 100}%;
  width: ${BOX_WIDTH * 100}%;
  height: ${BOX_HEIGHT * 100}%;
  background: #fff;
  padding: ${PAD_Y_CQW}cqw ${PAD_X_CQW}cqw;
  overflow: hidden;
  font-size: ${FONT_CQW}cqw;
  line-height: ${LINE_HEIGHT};
  color: #3d3d3d;
  text-align: left;
`;

const Caret = styled.span<{ $settled?: boolean }>`
  display: inline-block;
  width: 2px;
  height: 0.95em;
  margin-left: 1px;
  vertical-align: baseline;
  background: #3d3d3d;
  opacity: 1;
  ${({ $settled }) =>
    !$settled &&
    css`
      animation: ${blink} 1s steps(1) infinite;
    `}
`;

export interface TypedQuizNoteProps {
  text: string;
}

export const TypedQuizNote: React.FC<TypedQuizNoteProps> = ({ text }) => {
  // Decided once at mount (same gate as BoardChipTabs' smooth-scroll):
  // static render for reduced-motion users and automated browsers.
  const prefersStatic = useMemo(
    () =>
      (typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) ||
      (typeof navigator !== 'undefined' && navigator.webdriver === true),
    [],
  );

  const [started, setStarted] = useState(false);
  const [count, setCount] = useState(() => (prefersStatic ? text.length : 0));
  const ref = useRef<HTMLDivElement | null>(null);

  // Arm on first reveal. Environments without IntersectionObserver just
  // start immediately — the observer is a nicety, not a gate.
  useEffect(() => {
    if (prefersStatic) return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setStarted(true);
      return undefined;
    }
    // Chromium's initial notification (fired at observe() time, before any
    // scroll) can report isIntersecting: true without the ratio having
    // crossed the threshold yet, and even a ratio-gated first callback can
    // land during a transient early-layout frame that the page corrects a
    // moment later (Kate #818 re-QA on c38d6503: the note box measured 100%
    // visible at 31ms into load, then settled at 20% by 126ms). Require the
    // ratio to stay >= 0.5 continuously for VISIBILITY_DWELL_MS before
    // arming — any dip below cancels the pending arm — so a load-time blip
    // can't pass as a real scroll-into-view.
    let dwellTimer: number | undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        // A single callback can batch several queued entries for this one
        // observed element (the browser coalesces updates it didn't get to
        // dispatch individually). `entries.some(...)` would arm on a stale
        // high reading even if the latest one in the batch dropped back
        // below threshold, and would let a stale high reading keep an
        // in-progress dwell alive through a real dip. Only the most recent
        // entry — the last one in the array — reflects current visibility.
        const latest = entries[entries.length - 1];
        const visible = latest.intersectionRatio >= 0.5;
        if (visible) {
          if (dwellTimer === undefined) {
            dwellTimer = window.setTimeout(() => {
              setStarted(true);
              obs.disconnect();
            }, VISIBILITY_DWELL_MS);
          }
        } else if (dwellTimer !== undefined) {
          window.clearTimeout(dwellTimer);
          dwellTimer = undefined;
        }
      },
      { threshold: 0.5 },
    );
    obs.observe(el);
    return () => {
      if (dwellTimer !== undefined) window.clearTimeout(dwellTimer);
      obs.disconnect();
    };
  }, [prefersStatic]);

  // One typing pass, once: start delay, then a character each interval. The
  // interval self-clears at the end of the text (inside the updater —
  // idempotent, so StrictMode's double-invoke is safe). No re-arm — the note
  // types once and holds (Kate 2026-10-20).
  useEffect(() => {
    if (!started || prefersStatic) return undefined;
    let interval: number | undefined;
    const startTimer = window.setTimeout(() => {
      interval = window.setInterval(() => {
        setCount((prev) => {
          const next = prev + 1;
          if (next >= text.length && interval !== undefined) {
            window.clearInterval(interval);
          }
          return Math.min(next, text.length);
        });
      }, TYPE_INTERVAL_MS);
    }, TYPE_START_DELAY_MS);
    return () => {
      window.clearTimeout(startTimer);
      if (interval !== undefined) window.clearInterval(interval);
    };
  }, [started, prefersStatic, text.length]);

  const settled = prefersStatic || count >= text.length;

  return (
    <ImagePlane aria-hidden="true" data-testid="typed-quiz-note">
      <Patch ref={ref}>
        {text.slice(0, count)}
        {!prefersStatic && (
          <Caret
            $settled={settled}
            data-testid="typed-quiz-caret"
            data-settled={settled}
          />
        )}
      </Patch>
    </ImagePlane>
  );
};
